/**
 * RF-07 · Resultados consolidados (`/api/resultados`).
 *
 * ⚠️ Sin `id_ejecucion` los resultados agregan TODOS los programas y periodos que
 * hayan ejecutado la versión. No da error: da un promedio que mezcla áreas.
 *
 * Los promedios por componente NO aplican los pesos del árbol: son lectura de
 * avance, no la nota oficial.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { ResultadoIndicador, ResumenVersion, ResultadoComponente } from '@/interfaces/ResultadoVersion'
import { http } from '@/services/http'

/**
 * Consulta resultados · `GET /api/versiones`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns ResultadoIndicador[]
 */
export async function getResultadosVersionService(idVersion: number): Promise<ResultadoIndicador[]> {
  const result = await http.get<any>(`/api/versiones/${idVersion}/resultados`)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

/**
 * Consulta resultados · `GET /api/versiones`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns ResultadoIndicador | null
 */
export async function getResultadoIndicadorService(idVersion: number, idIndicador: number): Promise<ResultadoIndicador | null> {
  const result = await http.get<ResultadoIndicador>(`/api/versiones/${idVersion}/resultados/${idIndicador}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta resultados · `GET /api/versiones`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns ResumenVersion | null
 */
export async function getResumenVersionService(idVersion: number): Promise<ResumenVersion | null> {
  const result = await http.get<ResumenVersion>(`/api/versiones/${idVersion}/resultados/resumen`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta resultados · `GET /api/versiones`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns ResultadoComponente | null
 */
export async function getResultadoComponenteService(idVersion: number, idComponente: number): Promise<ResultadoComponente | null> {
  const result = await http.get<ResultadoComponente>(`/api/versiones/${idVersion}/resultados/componente/${idComponente}`)
  return result.ok ? (result.data ?? null) : null
}
